import Box from '@/common/components/Box';
import Button from '@/common/components/Button';
import Checkbox from '@/common/components/Checkbox';
import MaskedTextField from '@/common/components/MaskedTextField';
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple';
import SelectController from '@/common/components/SelectController';
import TextField from '@/common/components/TextField';
import { nameMask, phoneMask } from '@/common/constants/masks.constant';
import { useBranches } from '@/common/hooks/queries/useBranches';
import { useRoles } from '@/common/hooks/queries/useRoles';
import { useGoBack } from '@/common/hooks/useGoBack';
import { Mapper } from '@/common/services/mapper';
import { RoleData } from '@/common/types/roles';
import { FormTransformers } from '@/common/utils/formTransformers';
import { useCreateClientUser } from '@/features/users/hooks/useCreateClientUser';
import {
  clientUserSchema,
  ClientUserValidationSchema,
} from '@/features/users/schemas/create.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeftIcon } from 'lucide-react';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { statusOptions } from '../../constants/statusOptions.constant';
import { ImageUploader } from '@/common/components/ImageUploader';
import { checkIsImage } from '@/common/utils/fileValidator';
import { useDriverLicenseCategories } from '@/common/hooks/queries/useDriverLicenseCategories';
import { DriverLicenseCategoryData } from '@/common/types/driverLicenseCategory';
import useDebounce from '@/common/hooks/useDebounce';

const Create = () => {
  const goBack = useGoBack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<Blob>();

  const [userId, setUserId] = useState('');
  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const [searchCompanyBranches, setSearchCompanyBranches] = useDebounce('');
  const [searchRoles, setSearchRoles] = useDebounce('');
  const [searchDriverLicenseCategories, setSearchDriverLicenseCategories] = useDebounce('');

  const { data: rolesOptions } = useRoles({
    filters: { search: searchRoles },
    mapper: Mapper.mapToOptions<RoleData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  });

  const { data: branchOptions } = useBranches({
    filters: {
      search: searchCompanyBranches
    },
    mapper: Mapper.mapToOptions<RoleData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  });

  const { data: driverLicenseCategoriesOptions } = useDriverLicenseCategories({
    filters: { search: searchDriverLicenseCategories },
    mapper: Mapper.mapToOptions<DriverLicenseCategoryData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const {
    register,
    handleSubmit,
    control,
    setError,
    watch,
    formState: { errors },
  } = useForm<ClientUserValidationSchema>({
    resolver: zodResolver(clientUserSchema),
    defaultValues: {
      send_passwords_on_creation: true
    }
  });

  const sendPassword = watch('send_passwords_on_creation');

  const {
    mutate: createClientUser,
    isPending: createClientUserIsPending,
  } = useCreateClientUser({
    file,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['clientUsers'] });

      const data = response.data.data;

      if (data.id) {
        setUserId(data.id);
      }

      setIsError(false);


      if (sendPassword) {
        setMessages(['Senha enviada ao usuário por e-mail e/ou SMS.']);
      } else navigate(`/users/${data.id}`)
    },
    onError: (error) => {
      FormTransformers.errorsTransformer<ClientUserValidationSchema>({
        setError,
      })(error);

      setIsError(true);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const onSubmit: SubmitHandler<ClientUserValidationSchema> = (data) => {
    createClientUser(data);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-4 items-center">
            <Button
              variant="link-white"
              onClick={() => goBack({ fallback: '/users' })}
            >
              <ChevronLeftIcon size={40} strokeWidth={1} />
            </Button>
            <h2 className="text-2xl font-bold">Novo usuário</h2>
          </div>

          <div className="flex gap-4">
            <Button
              variant="outline-error"
              onClick={() => goBack({ fallback: '/users' })}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={createClientUserIsPending}
            >
              Confirmar
            </Button>
          </div>
        </div>
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto max-h-[calc(100vh-128px)]">
          <p className="text-lg font-bold">Dados pessoais</p>
          <ImageUploader
            isLoading={false}
            label="Foto"
            name="profile-picture"
            accept="image/*"
            onChange={(e) => {
              const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
              if (!file || !checkIsImage(file)) {
                return
              }
              setFile((e.target as HTMLInputElement & { files: FileList }).files[0])
            }}
            currentImageUrl={file
              ? URL.createObjectURL(file)
              : undefined
            }
            disabled={createClientUserIsPending}
            errorMessage={errors.profile_picture?.message as string | undefined}
          />
          <div className="flex gap-2 w-full justify-between">
            <div className="w-3/4">
              <MaskedTextField
                label="CPF"
                markAsRequired
                mask="000.000.000-00"
                errorMessage={errors?.cpf?.message || undefined}
                {...register('cpf')}
                disabled={createClientUserIsPending}
              />
            </div>
            <MaskedTextField
              label="Data de nascimento"
              markAsRequired
              mask="00/00/0000"
              className="w-1/4"
              errorMessage={errors.date_of_birth?.message || undefined}
              {...register('date_of_birth')}
              disabled={createClientUserIsPending}
            />
          </div>
          <div className="flex gap-2 w-full justify-between">
            <MaskedTextField
              label="Nome"
              markAsRequired
              mask={nameMask}
              maxLength={64}
              className="w-3/4"
              errorMessage={errors.full_name?.message || undefined}
              {...register('full_name')}
              disabled={createClientUserIsPending}
            />
            <SelectController
              control={control}
              name="user_roles"
              label="Perfil"
              markAsRequired
              className="w-1/4"
              errorMessage={errors.user_roles?.message || undefined}
              options={rolesOptions || []}
              onInputChange={setSearchRoles}
              isMulti
              isDisabled={createClientUserIsPending}
            />
          </div>
          <SelectController
            control={control}
            name="status"
            label="Status"
            markAsRequired
            errorMessage={errors.status?.message || undefined}
            options={statusOptions}
            isDisabled={createClientUserIsPending}
          />
          <TextField
            label="Matrícula"
            errorMessage={errors.enrollment_number?.message || undefined}
            {...register('enrollment_number')}
            disabled={createClientUserIsPending}
          />
          <SelectController
            control={control}
            name="branch_id"
            label="Filial"
            markAsRequired
            options={branchOptions || []}
            onInputChange={setSearchCompanyBranches}
            errorMessage={errors.branch_id?.message || undefined}
            isDisabled={createClientUserIsPending}
          />
          <MaskedTextField
            label="Telefone"
            mask={phoneMask}
            errorMessage={errors.phone?.number?.message || undefined}
            {...register('phone.number')}
            disabled={createClientUserIsPending}
          />
          <TextField
            label="Email"
            {...register('email')}
            errorMessage={errors.email?.message || undefined}
            disabled={createClientUserIsPending}
          />
          <Checkbox
            label="Enviar credenciais via E-mail/SMS"
            {...register("send_passwords_on_creation")}
            disabled={createClientUserIsPending}
          />

          <div className="flex gap-2 w-full justify-between">
            <MaskedTextField
              label="Número da CNH"
              mask="00000000000"
              className="grow"
              errorMessage={errors.current_driver_license_registration_number?.message || undefined}
              {...register('current_driver_license_registration_number')}
              disabled={createClientUserIsPending}
            />

            <SelectController
              control={control}
              name="user_driver_license_categories"
              label="Categorias da CNH"
              options={driverLicenseCategoriesOptions || []}
              onInputChange={setSearchDriverLicenseCategories}
              isMulti
              className="grow"
              errorMessage={errors.user_driver_license_categories?.message || undefined}
              isDisabled={createClientUserIsPending}
            />

            <MaskedTextField
              label="Data de vencimento"
              mask="00/00/0000"
              className="grow"
              errorMessage={errors.current_driver_license_expiration_date?.message || undefined}
              {...register('current_driver_license_expiration_date')}
              disabled={createClientUserIsPending}
            />
          </div>
        </Box>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages as string[]}
          setMessages={setMessages}
          isError={isError}
          onClose={isError ? undefined : () => navigate(`/users/${userId}`)}
        />
      )}
    </>
  );
};

export default Create;
